import axios from "axios";
import type { TranslateFn } from "@/api/utils/error";
import { MAX_UPLOAD_SIZE_BYTES } from "../constants/upload";

/**
 * Parse + map upload errors from the backend (BE PR #423, see
 * `brain/raw/reference/upload-error-handling.md`).
 *
 * The backend returns an envelope `{ errors: [{ code?, message, field? }] }` with a
 * dedicated status (413/415) and a machine-readable `code`. We branch **only on `code`**
 * (never on `message`, which is English and unstable — SPEC Constraints). Non-upload
 * errors return `null` so callers keep their existing behavior (AC 10).
 */

export type UploadApiError = {
  code: string;
  field: string | null;
  status: number | null;
};

/** A `code` we recognize as an upload rejection (branch target). */
const isUploadCode = (code: string): boolean =>
  code === "INVALID_FILE_TYPE" || code.startsWith("LIMIT_");

type UploadErrorEnvelopeItem = { code?: unknown; field?: unknown };

/**
 * Return `{ code, field, status }` when `error` is a recognizable upload rejection,
 * otherwise `null`. When a 413/415 arrives without an envelope `code`, the code is
 * synthesized from the status (secondary signal, D5).
 */
export const getUploadApiError = (error: unknown): UploadApiError | null => {
  if (!axios.isAxiosError(error)) return null;

  const status = error.response?.status ?? null;
  const data = error.response?.data as { errors?: unknown } | undefined;
  const items = Array.isArray(data?.errors)
    ? (data.errors as UploadErrorEnvelopeItem[])
    : null;
  const first = items?.[0];

  let code =
    typeof first?.code === "string" && first.code.length > 0
      ? first.code
      : null;
  const field =
    typeof first?.field === "string" && first.field.length > 0
      ? first.field
      : null;

  if (!code) {
    if (status === 413) code = "LIMIT_FILE_SIZE";
    else if (status === 415) code = "INVALID_FILE_TYPE";
  }

  if (!code || !isUploadCode(code)) return null;

  return { code, field, status };
};

/** Optional context for message interpolation. */
export type UploadErrorMessageContext = {
  /** Formats label for the failing field, e.g. `PDF, DOCX, PNG` (for 415). */
  allowedTypesLabel?: string;
  /**
   * The cap the failing field actually enforces, when it is not the document
   * one. Images are held to 2MB, and a 413 on a cover used to be unreachable
   * from the company settings page — now that it is, quoting the document limit
   * would send the user off to compress a file to a size that was never asked.
   */
  maxSizeMb?: number;
};

const DEFAULT_MAX_SIZE_MB = MAX_UPLOAD_SIZE_BYTES / (1024 * 1024);

/**
 * Map an upload `code` to a localized Italian message. Falls back to a generic
 * message for unknown or missing codes (AC 6, 9). Never reads the BE `message`.
 */
export const mapUploadErrorMessage = (
  code: string | null,
  t: TranslateFn,
  ctx?: UploadErrorMessageContext,
): string => {
  switch (code) {
    case "LIMIT_FILE_SIZE":
      return String(
        t("common.upload_errors.file_too_large", {
          max: ctx?.maxSizeMb ?? DEFAULT_MAX_SIZE_MB,
        }),
      );
    case "INVALID_FILE_TYPE":
      return String(
        t("common.upload_errors.invalid_file_type", {
          types: ctx?.allowedTypesLabel ?? "",
        }),
      );
    case "LIMIT_FILE_COUNT":
      return String(t("common.upload_errors.file_count"));
    case "LIMIT_UNEXPECTED_FILE":
      return String(t("common.upload_errors.unexpected"));
    default:
      return String(t("common.upload_errors.generic"));
  }
};
